import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { useApptToShow } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/hooks/useApptToShow'
import { appointmentIsSupported } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/utils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { CalendarAppointmentsView } from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'

import { MESSENGER_POPOUT } from '../common/constants/routingConstants'
import { getAlertsState } from '../data/alertsSelectors'
import { getAuthConfig, getAuthUserId } from '../data/auth/authSelectors'
import { useGetAppointmentsQuery } from '../lyraTherapy/clients/clientDetails/data/appointmentsApi'
import { getLTVideoSessionOpen } from '../lyraTherapy/data/ltVideoSelectors'

/**
 * Banner height constants for layout calculations
 * Used to calculate sticky element positioning when banners are visible
 */
const BANNER_HEIGHTS = {
  JOIN_SESSION: 90,
  ACTIVE_ALERT: 100,
} as const

// Export individual constants for backward compatibility
export const JOIN_BANNER_HEIGHT = BANNER_HEIGHTS.JOIN_SESSION
export const ACTIVE_ALERT_HEIGHT = BANNER_HEIGHTS.ACTIVE_ALERT

/**
 * Return type for useAllBannerVisibility hook
 */
interface BannerVisibilityState {
  /** True if any banner is currently visible */
  hasAnyBanner: boolean
  /** True if any session-related banner is visible (join or active video) */
  hasAnySessionBanner: boolean
  /** True if PSA (Public Service Announcement) banner is enabled */
  hasPSABanner: boolean
  /** True if there are active alert banners */
  hasActiveAlerts: boolean
  /** True if an active video session banner is showing */
  hasVideoSessionBanner: boolean
  /** True if the join session banner is showing */
  hasJoinSessionBannerOpen: boolean
}

/**
 * Comprehensive hook to detect when any banner is present at the top of the page.
 *
 * This hook consolidates all banner visibility logic across the provider portal,
 * including session banners, alert banners, and PSA banners. It's particularly
 * useful for calculating layout offsets and positioning sticky elements correctly.
 *
 * @returns {BannerVisibilityState} Object containing visibility state for all banner types
 *
 * @example
 * ```tsx
 * const { hasAnyBanner, hasAnySessionBanner, hasActiveAlerts } = useAllBannerVisibility()
 *
 * // Calculate sticky element offset
 * const topOffset = hasAnySessionBanner ? JOIN_BANNER_HEIGHT : 0
 * ```
 */
export const useAllBannerVisibility = (): BannerVisibilityState => {
  // Core state
  const videoSessionOpen = useSelector(getLTVideoSessionOpen)
  const userId = useSelector(getAuthUserId)
  const config = useSelector(getAuthConfig)
  const location = useLocation()
  const { psaBanner, isDAEngageOnOffPlatformVideoEnabled } = useFlags()

  // Fetch appointments for join session banner logic
  const { data: appointments } = useGetAppointmentsQuery({
    providerId: userId,
    durationRange: { before: { hours: 2 }, after: { hours: 4 } },
    view: CalendarAppointmentsView.CALENDAR,
  })

  // Filter to supported video appointments
  const supportedAppointments = useMemo(
    () =>
      appointments?.filter(
        (appt) =>
          appointmentIsSupported(appt, isDAEngageOnOffPlatformVideoEnabled) &&
          appt.meetingFormat === MEETING_FORMATS.VIDEO,
      ) ?? [],
    [appointments, isDAEngageOnOffPlatformVideoEnabled],
  )

  // Get appointment to show in join banner (within 10 minute reminder window)
  const { apptToShow } = useApptToShow({ appts: supportedAppointments, reminderWindow: 10 })

  // Calculate join session banner visibility
  // Banner shows when ALL conditions are met:
  const isPopout = location.pathname.includes(MESSENGER_POPOUT.route)
  const hasJoinSessionBannerOpen =
    !!apptToShow?.userInfo && // Has appointment with user info
    !videoSessionOpen && // No active video session
    !isPopout && // Not in popout window (/messages/popout)
    !!config?.videoSessionsEnabled // Video sessions enabled for user role (already includes DA flag state)

  // Video session banner visibility
  const hasVideoSessionBanner = videoSessionOpen

  // Combined session banner visibility
  const hasAnySessionBanner = hasJoinSessionBannerOpen || hasVideoSessionBanner

  // PSA banner visibility
  const hasPSABanner = psaBanner?.enabled ?? false

  // Alert banner visibility
  const alerts = useSelector(getAlertsState)
  const hasActiveAlerts = Array.isArray(alerts) && alerts.some((alert: any) => alert.show)

  // Memoize the return value to prevent unnecessary re-renders
  return useMemo(() => {
    const hasAnyBanner = hasAnySessionBanner || hasPSABanner || hasActiveAlerts

    return {
      hasAnyBanner,
      hasAnySessionBanner,
      hasPSABanner,
      hasActiveAlerts,
      hasVideoSessionBanner,
      hasJoinSessionBannerOpen,
    }
  }, [hasAnySessionBanner, hasPSABanner, hasActiveAlerts, hasVideoSessionBanner, hasJoinSessionBannerOpen])
}
